import "./entity-capture.scss";
import { useContext, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { BsSave } from "react-icons/bs";
import { useParams } from "react-router";
import { useNavigate } from "react-router";

import ConfirmationPopup from "../../../shared/components/confirmation-popup/confirmation-popup";
import Spinner from "../../../shared/components/spinner/spinner";
import ToolTip from "../../../shared/components/tooltip/tooltip";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import { useHttpGet } from "../../../shared/hooks/use-httpNonTable";
import { failedToast, getAllData, getData, postSingleNonTable } from "../../../shared/utils/api";
import { usePrompt } from "../../../shared/utils/use-prompt";
import { createUUID, IObject, validateLoadingStatusNew } from "../../../shared/utils/utils";
import MultiTabComponent, { IMultiTabComponentConfig } from "../../components/multi-tab-component/multi-tab-component";
import Page from "../../components/page/page";
import { Widget } from "../../components/widgets-layout/widget-utils";
import ScreenSetup, { IScreenSetupData } from "./steps/screen-setup";
import ScreenConfiguration, { IScreenConfigurationData } from "./steps/screen-configuration";

const EntityCapture = () => {
  const params = useParams();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);

  const [setupScreenData, setSetupScreenData] = useState<IScreenSetupData>({name: "", entity: "", entity_id: ""});
  const [screenConfigData, setScreenConfigData] = useState<IScreenConfigurationData>({layout: [], layoutItems: []});
  const [primaryKeys, setPrimaryKeys] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const { flag, setFlag, next } = usePrompt(isDirty);    

  const { sendRequest: postScreen, status: postScreenStatus, error: postScreenError } = useHttp(postSingleNonTable, false, authCtx.token, "capture",  "manager");
  const { sendRequest: getScreen, status: getScreenStatus, data: getScreenData, error: getScreenError } = useHttp(getAllData, false, authCtx.token, "capture", "manager");
  const { sendRequest: getEntity, status: getEntityStatus, data: getEntityData, error: getEntityError } = useHttpGet(getData, true, authCtx.token, "entity", "platform");

  const pageIsEdit = !!params.id;

  useEffect(() => {
    if (pageIsEdit) {
      getScreen(`capture/${params.id}`);
    }
  }, []);

  useEffect(() => {
    if (getEntityData && !getEntityError) {
      const primaryKeys = getEntityData.rowData?.filter((row: IObject) => row.primary_key).map((row: IObject) => row.input_field_name) || [];
      setPrimaryKeys(primaryKeys);
    }
  }, [getEntityStatus, getEntityError, getEntityData]);

  useEffect(() => {
    if (getScreenData && !getScreenError) {
      setSetupScreenData({
        name: getScreenData.name,
        entity: getScreenData.datamodel_name,
        entity_id: getScreenData.datamodel_id
      });

      setScreenConfigData({
        layout: getScreenData.layout.layout,
        layoutItems: getScreenData.layout.layoutItems
      });
    }
  }, [getScreenStatus]);

  useEffect(() => {
    if (!pageIsEdit) {
      setScreenConfigData({layout: [], layoutItems: []});
    }
    if (setupScreenData.entity_id) {
      getEntity(`datamodel_handle_referencing_field/entity/${setupScreenData.entity_id}`);
    }
  }, [setupScreenData.entity_id]);

  useEffect(() => {
    if (postScreenStatus === "completed" && !postScreenError) {
      navigate("/manager/entity_capture_screens");
    }
  }, [postScreenStatus]);

  const handleSetupScreenDataChange = (data: IScreenSetupData) => {
    setIsDirty(true);
    setSetupScreenData(data);
  };

  const handleScreenSave = () => {
    const primaryKeysNotFound = primaryKeys.filter(pk => !screenConfigData.layoutItems.some((item: Widget) => item.id === pk));
    if (primaryKeysNotFound.length > 0) {
      failedToast(`Primary keys ${primaryKeysNotFound.join(", ")} not found in layout`);
      return;
    }
    if (setupScreenData.name === "") {
      failedToast("Screen name is required");
      return;
    }

    const screenId = pageIsEdit ? params.id : createUUID();
    postScreen(`capture/${screenId}`, {
      id: screenId,
      name: setupScreenData.name,
      datamodel_id: setupScreenData.entity_id,
      layout: {
        layout: screenConfigData.layout,
        layoutItems: screenConfigData.layoutItems,
      },
      datamodel_type: "entity"});
    setIsDirty(false);
  };

  const tabConfig: IMultiTabComponentConfig = {
    tabs: [{
      key: "screenSetup",
      name: "Screen Setup",
      content: <ScreenSetup screenData={setupScreenData} handleDataChange={handleSetupScreenDataChange}/>
    },
    {
      key: "layoutConfiguration",
      name: "Layout Configuration",
      content: <ScreenConfiguration layout={{layout: screenConfigData.layout, layoutItems: screenConfigData.layoutItems}} selectedEntity={setupScreenData.entity_id} handleDataChange={setScreenConfigData}/>,
      isVisible: () => setupScreenData.entity !== ""
    }]
  };

  return <Page title={{
    text: setupScreenData.name,
    onTitleEdited: (title: string) => {
      setSetupScreenData({
        ...setupScreenData,
        name: title
      });
    },
    placeholder: "Entity Capture Screen Name"
  }}
  breadCrumbItems={[
    {
      name: "Entity Capture Screens",
      url: "/manager/entity_capture_screens"
    },
    {
      name: "Entity Capture Screen"
    }
  ]}
  >
    <div className="entityCapture">
      {validateLoadingStatusNew([postScreenStatus, getScreenStatus]) && <Spinner />}
      <div className="entityCapture-saveButton">
        <ToolTip content={"Save"} direction="top" delay={350}  >
          <Button disabled={postScreenStatus === "pending"} name="save" className="entityCapture-save" onClick={handleScreenSave}>
            <BsSave />
          </Button>
        </ToolTip>
      </div>
      <MultiTabComponent {...tabConfig}/>

      {flag && <ConfirmationPopup 
        show={flag} 
        handleSaveClick={() =>handleScreenSave()} 
        handlePopupClosed={() => setFlag(false)} 
        handleContinue={() => { next(); setFlag(false); }} />}

    </div>
  </Page>;
};

export default EntityCapture;